import React, { useEffect, useId, useState } from 'react';
import WaButton from '../../wa/WaButton';
import TeacherPicker from './TeacherPicker';
import {
	FLAG_CONFLICT,
	FLAG_REPEAT,
	FLAG_UNASSIGNED,
	LOCATION_LABELS,
	formatEventRange,
	formatEventTime,
	isSettledFlag,
	liveFlags,
	topFlag,
} from './schedulingModel';

/**
 * One day at one location — the unit the generator assigns a single teacher to,
 * and therefore one card.
 *
 * Everything needed to review-and-lock lives on the card: the picker, the lock,
 * the status chips and the rotation memory. That is the design's core claim
 * (the reviewer never leaves the board), so resist moving any of it into a
 * drawer later.
 *
 * NO HOVER-ONLY AFFORDANCES. Every control is rendered at full opacity and
 * muted with colour if it needs to recede — the portal's primary user has
 * keratoconus, and the installed PWA has no hover at all. See CLAUDE.md.
 */

const CHIP = {
	[FLAG_CONFLICT]: { className: 'is-conflict', label: 'Soft conflict' },
	[FLAG_REPEAT]: { className: 'is-repeat', label: 'Rotation repeat' },
	[FLAG_UNASSIGNED]: { className: 'is-unassigned', label: 'Unassigned' },
};

function DayLocationCard({
	block,
	candidates,
	flags,
	rotation,
	teacherNameById,
	condensed,
	expandLessons,
	isAdmin,
	busy,
	onTeacherChange,
	onToggleLock,
	onSave,
	hasPendingChange,
}) {
	// Seeded from the page-level toggle rather than always false, so a card that
	// mounts while "Show lessons" is on opens already expanded — scrolling a month
	// must not reveal collapsed rows behind the toggle that claims to have opened
	// everything.
	const [expanded, setExpanded] = useState(Boolean(expandLessons));
	const listId = useId();
	const lessons = block.lessons || [];

	// The page toggle SETS every card rather than overriding them: with a plain
	// `expandLessons || expanded` the per-card control would appear dead while the
	// global one was on, which is worse than either behaviour on its own. This way
	// "Show lessons" opens (or closes) all of them and each card stays independently
	// clickable afterwards.
	useEffect(() => {
		setExpanded(Boolean(expandLessons));
	}, [expandLessons]);

	// A LIVE flag asks for a change. A settled one (a repeat on a day that has
	// passed, schedulingModel.isSettledFlag) keeps its colour and asks nothing,
	// so the card condenses like a clean one in review mode.
	const flag = topFlag(liveFlags(flags));
	const settled = flag ? null : topFlag((flags || []).filter(isSettledFlag));
	const locationClass =
		block.location === 'LAKEVILLE' ? 'is-lakeville' : block.location === 'HLDC' ? 'is-hldc' : '';
	const shown = flag || settled;
	const stateClass = shown
		? ` is-flagged ${CHIP[shown.type]?.className || ''}${settled ? ' is-settled' : ''}`
		: '';

	const rotationText = [
		rotation.lastWeek ? `Last wk ${teacherNameById.get(rotation.lastWeek) || '—'}` : null,
		rotation.priorWeek ? `prior ${teacherNameById.get(rotation.priorWeek) || '—'}` : null,
	]
		.filter(Boolean)
		.join(' · ');

	// A clean, locked block in review mode collapses to one line. Flagged blocks
	// never condense — that asymmetry IS the review mode. A settled repeat
	// condenses too, keeping its yellow and saying why in words.
	if (condensed && !flag) {
		return (
			<article className={`ts-card ts-card--condensed ${locationClass}${stateClass}`} data-block-key={block.key}>
				<span className='ts-card__loc'>{LOCATION_LABELS[block.location] || block.location}</span>
				<span className='ts-card__meta'>
					{block.lessonCount} {block.lessonCount === 1 ? 'lesson' : 'lessons'}
				</span>
				<span className='ts-card__who'>
					{block.teacherId ? teacherNameById.get(block.teacherId) || block.teacherId : 'Unassigned'}
				</span>
				{settled && (
					<span className='ts-chip is-settled' title={settled.reasons.join('; ')}>
						<i className='fa-solid fa-rotate' aria-hidden='true' /> Repeat, date passed
					</span>
				)}
				{block.isLocked && (
					<span className='ts-chip is-locked'>
						<i className='fa-solid fa-lock' aria-hidden='true' /> Locked
					</span>
				)}
				{expanded && lessons.length > 0 && (
					<ul className='ts-lessons ts-lessons--condensed'>
						{lessons.map((lesson) => (
							<li className='ts-lessons__row' key={lesson.eventId}>
								<span className='ts-lessons__time'>{formatEventTime(lesson.startTs)}</span>
								<span className='ts-lessons__student'>{lesson.studentName}</span>
								<span
									className={`ts-lessons__who${lesson.teacherId ? '' : ' is-unassigned'}`}>
									{lesson.teacherId
										? teacherNameById.get(lesson.teacherId) || lesson.teacherId
										: 'Unassigned'}
								</span>
							</li>
						))}
					</ul>
				)}
			</article>
		);
	}

	return (
		<article className={`ts-card ${locationClass}${stateClass}`} data-block-key={block.key}>
			<header className='ts-card__head'>
				<span className='ts-card__loc'>{LOCATION_LABELS[block.location] || block.location}</span>
				{/* The lesson count and range double as the disclosure control — the
				    thing you want detail about IS the thing you click. A real
				    <button> so keyboard and screen readers get it for free; the
				    explicit `height:auto` in the stylesheet is load-bearing, because
				    native.css locks every bare <button> to a fixed 30px BY LAYER and
				    a class selector alone does not beat it (CLAUDE.md). */}
				{lessons.length > 0 ? (
					<button
						type='button'
						className='ts-card__meta ts-card__disclose'
						aria-expanded={expanded}
						aria-controls={listId}
						onClick={() => setExpanded((v) => !v)}>
						<i
							className={`fa-solid ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'} ts-card__chev`}
							aria-hidden='true'
						/>
						{block.lessonCount} {block.lessonCount === 1 ? 'lesson' : 'lessons'}
						{block.startTs && block.endTs && (
							<> · {formatEventRange(block.startTs, block.endTs)}</>
						)}
					</button>
				) : (
					<span className='ts-card__meta'>
						{block.lessonCount} {block.lessonCount === 1 ? 'lesson' : 'lessons'}
						{block.startTs && block.endTs && (
							<> · {formatEventRange(block.startTs, block.endTs)}</>
						)}
					</span>
				)}
			</header>

			{expanded && lessons.length > 0 && (
				<ul className='ts-lessons' id={listId}>
					{lessons.map((lesson) => {
						// Resolved per lesson, so a timeslot-rule override reads as the
						// teacher who is actually taking it rather than the block's owner.
						const who = lesson.teacherId
							? teacherNameById.get(lesson.teacherId) || lesson.teacherId
							: 'Unassigned';
						const differs =
							Boolean(block.teacherId) &&
							Boolean(lesson.teacherId) &&
							lesson.teacherId !== block.teacherId;
						return (
							<li className='ts-lessons__row' key={lesson.eventId}>
								<span className='ts-lessons__time'>{formatEventTime(lesson.startTs)}</span>
								<span className='ts-lessons__student'>{lesson.studentName}</span>
								<span
									className={`ts-lessons__who${lesson.teacherId ? '' : ' is-unassigned'}${
										differs ? ' is-override' : ''
									}`}>
									{who}
									{lesson.isLocked && (
										<i
											className='fa-solid fa-lock ts-lessons__lock'
											aria-label='Locked'
											title='Locked'
										/>
									)}
								</span>
							</li>
						);
					})}
				</ul>
			)}

			{shown && (
				<p className={`ts-card__strip ${CHIP[shown.type]?.className || ''}`} role='status'>
					<i
						className={`fa-solid ${shown.type === FLAG_CONFLICT ? 'fa-triangle-exclamation' : 'fa-rotate'}`}
						aria-hidden='true'
					/>
					<span>
						{shown.teacherName ? `${shown.teacherName} ` : ''}
						{shown.reasons.join('; ')}
						{settled ? '. The date has passed, so nothing needs changing.' : ''}
					</span>
				</p>
			)}

			<div className='ts-card__controls'>
				<TeacherPicker
					id={`picker-${block.key}`}
					value={block.teacherId}
					candidates={candidates}
					onChange={(teacherId) => onTeacherChange(block, teacherId)}
					disabled={!isAdmin || busy}
					ariaLabel={`Teacher for ${block.dayKey} ${block.location}`}
				/>

				{/* The locked state rides `variant`, a plain string attribute, rather
				    than a className — React's reflection of className onto a custom
				    element is not dependable here (see TeacherScheduling.css). */}
				<WaButton
					variant={block.isLocked ? 'brand' : undefined}
					size='small'
					onClick={() => onToggleLock(block, !block.isLocked)}
					disabled={!isAdmin || busy || block.assignments.length === 0}>
					<i
						className={`fa-solid ${block.isLocked ? 'fa-lock' : 'fa-lock-open'}`}
						aria-hidden='true'
					/>
					{block.isLocked ? 'Locked' : block.isPartiallyLocked ? 'Part-locked' : 'Lock'}
				</WaButton>
			</div>

			<div className='ts-card__chips'>
				{block.manualOverride && <span className='ts-chip is-manual'>Manual · kept</span>}
				{block.isMixed && <span className='ts-chip is-mixed'>Split day</span>}
				{block.notes && <span className='ts-chip is-note'>{block.notes}</span>}
			</div>

			{rotationText && <p className='ts-card__rotation'>{rotationText}</p>}

			{hasPendingChange && (
				<div className='ts-card__save'>
					<WaButton
						size='small'
						variant='brand'
						onClick={() => onSave(block)}
						disabled={!isAdmin || busy}>
						Save this block
					</WaButton>
				</div>
			)}
		</article>
	);
}

export default DayLocationCard;
